import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExploitBlockRuleCategory, ExploitBlockRuleWithStatus } from '../../types/exploit-rules';
import { CategoryIcon } from './CategoryIcon';
import { GlobalRuleRow } from './GlobalRuleRow';

interface GlobalCategoryRulesSectionProps {
  category: ExploitBlockRuleCategory;
  onToggleRule: (rule: ExploitBlockRuleWithStatus) => void;
  isPending: boolean;
  showCategoryHeader: boolean;
  formatDate: (dateStr: string) => string;
}

export function GlobalCategoryRulesSection({
  category,
  onToggleRule,
  isPending,
  showCategoryHeader,
  formatDate,
}: GlobalCategoryRulesSectionProps) {
  const { t } = useTranslation('exploitRules');
  const [isExpanded, setIsExpanded] = useState(true);
  const enabledCount = category.rules?.filter((r) => !r.globally_disabled).length || 0;
  const disabledCount = (category.rules?.length || 0) - enabledCount;

  // Get translated category name and description
  const categoryName = t(`categories.${category.id}.name`, { defaultValue: category.name });
  const categoryDescription = t(`categories.${category.id}.description`, { defaultValue: category.description });

  return (
    <div>
      {showCategoryHeader && (
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b dark:border-slate-700 px-4 py-3 flex items-center justify-between z-10">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-3"
          >
            <CategoryIcon name={category.id} />
            <div className="text-left">
              <div className="font-semibold text-gray-900 dark:text-white">{categoryName}</div>
              <div className="text-xs text-gray-500 dark:text-slate-400">
                {categoryDescription} | {t('policyManager.ruleCount', { count: category.rules?.length || 0 })}
                {disabledCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400 ml-1">{t('global.globallyDisabled', { count: disabledCount })}</span>
                )}
              </div>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}

      {(!showCategoryHeader || isExpanded) && (
        <div className="divide-y divide-gray-100 dark:divide-slate-700/50">
          {category.rules?.map((rule) => (
            <GlobalRuleRow
              key={rule.id}
              rule={rule}
              onToggle={() => onToggleRule(rule)}
              isPending={isPending}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
